import React, { useEffect, useState, useRef } from "react";
import { Autocomplete, TextField, CircularProgress } from "@mui/material";
import { PortalService } from "services/portal";

/**
 * Selector de cliente (empresa) con búsqueda en servidor. Reemplaza al
 * MemberPicker que cargaba 100 fijos y filtraba en memoria — ese cortaba
 * el listado alfabéticamente y dejaba afuera empresas como "MOCCA IA"
 * cuando había muchos clientes.
 *
 * Props:
 *   value: memberId actualmente seleccionado (string)
 *   onChange: (memberId | null) => void
 *   label: texto del input
 *   placeholder: texto cuando está vacío
 */
export function MemberPicker({ value, onChange, label = "Cliente", placeholder = "Busca por nombre o RUT" }) {
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const debounceRef = useRef(null);

  // Cuando cambia el value externamente, intentamos resolver el member
  // para mostrar su nombre. Si ya está en options lo usamos.
  useEffect(() => {
    if (!value) {
      setSelected(null);
      return;
    }
    const fromList = options.find((o) => o._id === value);
    if (fromList) {
      setSelected(fromList);
      return;
    }
    // No está en options — pide todos para resolverlo.
    PortalService.listPortalMembers({ search: "", limit: 500 })
      .then((res) => {
        const m = (res.items || []).find((o) => o._id === value);
        if (m) setSelected(m);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Búsqueda debounced cuando cambia el input.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await PortalService.listPortalMembers({
          search: inputValue.trim(),
          limit: 500, // trae TODOS — el server cap a 500 y la UI con virtual scroll lo maneja bien
        });
        setOptions(res.items || []);
      } catch (e) {
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [inputValue]);

  return (
    <Autocomplete
      size="small"
      sx={{ minWidth: 360 }}
      options={options}
      loading={loading}
      value={selected}
      inputValue={inputValue}
      onInputChange={(_, v) => setInputValue(v)}
      onChange={(_, opt) => {
        setSelected(opt);
        onChange(opt?._id || null);
      }}
      isOptionEqualToValue={(opt, v) => opt?._id === v?._id}
      getOptionLabel={(opt) => (opt ? `${opt.id_number} — ${opt.name}` : "")}
      filterOptions={(x) => x /* no filtrar client-side: confía en el server */}
      noOptionsText={inputValue ? `Sin resultados para "${inputValue}"` : "Escribe para buscar..."}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
